import { ExchangeStatus } from '@drift-labs/sdk';
import { matchEnum } from '@drift/common';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { useLocalStorageStringState } from 'src/hooks/useLocalStorageState';
import usePnlModalDisplayListener from 'src/hooks/usePnlModalDisplayListener';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import { useShallow } from 'zustand/react/shallow';
import useShowInsuranceWarningForMarket from '../hooks/useShowInsuranceWarningForMarket';
import DevModal from './DevModal';
import { DownloadModal } from './Downloader';
import AccountHealthModal from './Modals/AccountHealthModal';
import AssetDetailsModal from './Modals/AssetDetailsModal';
import CollateralModal from './Modals/CollateralModal';
import CreateReferralLinkModal from './Modals/CreateReferralLinkModal';
import DelegateModal from './Modals/DelegateModal';
import DeleteSubaccountModal from './Modals/DeleteSubaccountModal';
import EditOrderModal from './Modals/EditOrderModal';
import EditSubaccountModal from './Modals/EditSubaccountModal';
import ExchangePausedModal from './Modals/ExchangePausedModal';
import IFPerformanceModal from './Modals/IFPerformanceModal';
import InsuranceFundDetailsModal from './Modals/InsuranceFundDetailsModal';
import InsuranceFundRevenueModal from './Modals/InsuranceFundRevenueModal';
import LPDetailsModal from './Modals/LPDetailsModal';
import LPModal from './Modals/LPModal';
import LpPerformanceModal from './Modals/LpPerformanceModal';
import MarketDetailsModal from './Modals/MarketDetailsModal';
import NewLpSubaccountModal from './Modals/NewLpSubaccountModal';
import NewSubaccountModal from './Modals/NewSubaccountModal';
import NoInsuranceFundForMarketWarningModal from './Modals/NoInsuranceFundForMarketWarningModal';
import PnlModal from './Modals/PnlModal';
import SettingsModal from './Modals/SettingsModal';
import StakeModal from './Modals/StakeModal';
import TradeDetailsModal from './Modals/TradeDetailsModal';
import UserCumFundingModal from './Modals/UserCumFundingModal';
import AccountSidebarModal from './Modals/AccountSidebarModal/AccountSidebarModal';
import SendReceiveModal from './Modals/SendFromWalletModal';
import ReceiveToWalletModal from './Modals/ReceiveToWalletModal';
import PageBottomLoadingBar from './PageBottomLoadingBar/PageBottomLoadingBar';
import FeatureAlerts from './PopupAlert/FeatureAlerts';
import SweepstakesAnnouncementModal from './Sweepstakes/SweepstakesAnnouncementModal';
import SweepstakeWinnerModal from './Sweepstakes/SweepstakesWinnerModal';
import ToastList from './Toasts/ToastList';
import WalletConnectModal from './WalletConnectModal';
import LiquidationPanel from './Modals/HistoryPanels/LiquidationPanel';
import OrderPanel from './Modals/HistoryPanels/OrdersPanel';
import CloseAllPositionsModal from './Modals/CloseAllPositionsModal';
import RateHistoryModal from './Modals/RateHistoryModal';
import DlpIntroModal from './Modals/DlpIntroModal';
import useAppEventEmitter from '../hooks/useAppEventEmitter';
import { DriftModalKey } from '../stores/DriftStore/driftStoreTypes';
import useDriftActions from '../hooks/useDriftActions';
import LeaguesModal from './DriftPoints/LeaguesModal';
import { PreLaunchMarketWarningAlert } from './PreLaunchMarketAlerts/PreLaunchMarketWarningAlert';
import useShowPreLaunchMarketWarningAlert from '../hooks/useShowPreLaunchMarketWarningAlert';

/**
 * Component responsible for rendering general modals, loading indicators, notifications, etc. Anything which is rendered "seperately" to the core of the app. This is done to reduce the initial load bundle size.
 * @returns
 */
const FloatingUI = () => {
	// # Constants
	const setState = useDriftStore((s) => s.set);
	const loadingQueue = useDriftStore((s) => s.loadingElements);
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const pnlDisplayPosition = useDriftStore((s) => s.pnlModalPosition);
	const showInsuranceFundWarning =
		useShowInsuranceWarningForMarket().shouldShowWarning;
	const actions = useDriftActions();
	const appEventEmitter = useAppEventEmitter();

	useShowPreLaunchMarketWarningAlert();

	useEffect(() => {
		const handler = (modalType: DriftModalKey) => {
			actions.showModal(modalType);
		};

		appEventEmitter.on('showModal', handler);

		return () => {
			appEventEmitter.off('showModal', handler);
		};
	}, [appEventEmitter]);

	const {
		showDepositModal,
		showWithdrawModal,
		showBorrowModal,
		showDownloadModal,
		showLoadingBar,
		showNewSubaccountModal,
		showEditSubaccountModal,
		showDeleteSubaccountModal,
		showCollateralTransferModal,
		showPnlModal,
		showWalletConnectionModal,
		showDevModal,
		showSettingsModal,
		showNetworkModal,
		showOtherSettingsModal,
		showMarginModal,
		showEditOrderModal,
		showMarketDetailsModal,
		showInsuranceFundDetailsModal,
		showTradeDetailsModal,
		showCreateReferralLinkModal,
		showDelegateAccountModal,
		showStakeModal,
		showUnstakeModal,
		showAssetDetailsModal,
		showAddLiquidityModal,
		showRemoveLiquidityModal,
		showLpDetailsModal,
		showLpPerformanceModal7d,
		showLpPerformanceModal30d,
		showNewLpSubaccountModal,
		showIfPerformanceModal,
		showUserCumFundingModal,
		showAccountHealthModal,
		showInsuranceFundRevenueModal,
		showAccountsSidebarModal,
		showSendFromWalletModal,
		showReceiveToWalletModal,
		showSweepStakeModal,
		showSweepStakeWinnerModal,
		showCloseAllPositionsModal,
		rateHistoryModal,
		showDlpIntroModal,
		showLeaguesModal,
		showPreLaunchMarketWarningAlert,
	} = useDriftStore(useShallow((s) => s.modals));

	const [showLiquidationModal, showOrderModal] = useDriftStore(
		useShallow((s) => [
			s.historyPanels.liquidations.show,
			s.historyPanels.orders.show,
		])
	);

	const isMobile = useIsMobileScreenSize();

	// # State

	const router = useRouter();

	const canShowPauseBlock =
		router.route.includes('/redeem') ||
		router.route.includes('/terms') ||
		router.route.includes('/privacy') ||
		router.route.includes('/redemptionTerms');

	const [hideExchangePaused] = useLocalStorageStringState(
		'hideExchangePaused',
		'false'
	);

	const hideModal = useCallback((modalToHide: keyof DriftStore['modals']) => {
		setState((s) => {
			s.modals[modalToHide] = false;
		});
	}, []);

	// # Effect Hooks
	// # Rendering

	const initialDownloadType = useDriftStore((s) => s.downloadType);

	usePnlModalDisplayListener();

	const hideStakeModal = useCallback(() => {
		hideModal('showStakeModal');
	}, []);

	const hideUnstakeModal = useCallback(() => {
		hideModal('showUnstakeModal');
	}, []);

	return (
		<>
			{/* Render modals */}
			{!canShowPauseBlock &&
			hideExchangePaused !== 'true' &&
			driftClient &&
			driftClient.isSubscribed &&
			matchEnum(
				driftClient.getStateAccount().exchangeStatus,
				ExchangeStatus.PAUSED
			) ? (
				<ExchangePausedModal />
			) : showInsuranceFundWarning ? (
				<>
					<NoInsuranceFundForMarketWarningModal />
				</>
			) : showDevModal ? (
				<DevModal />
			) : showWalletConnectionModal ? (
				<WalletConnectModal />
			) : showSettingsModal ? (
				<SettingsModal />
			) : showNetworkModal ? (
				<SettingsModal settingGroupIndex={1} />
			) : showMarginModal ? (
				<SettingsModal settingGroupIndex={2} />
			) : showOtherSettingsModal ? (
				<SettingsModal settingGroupIndex={4} />
			) : showDepositModal ? (
				<CollateralModal
					onClose={() => {
						hideModal('showDepositModal');
					}}
				/>
			) : showWithdrawModal ? (
				<CollateralModal
					initialSide="withdraw"
					onClose={() => {
						hideModal('showWithdrawModal');
					}}
				/>
			) : showBorrowModal ? (
				<CollateralModal
					initialSide="borrow"
					onClose={() => {
						hideModal('showBorrowModal');
					}}
				/>
			) : showCollateralTransferModal ? (
				<CollateralModal
					initialSide="manage"
					onClose={() => {
						hideModal('showCollateralTransferModal');
					}}
				/>
			) : showStakeModal ? (
				<StakeModal onClose={hideStakeModal} />
			) : showUnstakeModal ? (
				<StakeModal initialSide="unstake" onClose={hideUnstakeModal} />
			) : showAddLiquidityModal ? (
				<LPModal />
			) : showRemoveLiquidityModal ? (
				<LPModal initialSide="remove" />
			) : showDownloadModal ? (
				<DownloadModal initialType={initialDownloadType} />
			) : showNewSubaccountModal ? (
				<NewSubaccountModal />
			) : showEditSubaccountModal ? (
				<EditSubaccountModal />
			) : showDeleteSubaccountModal ? (
				<DeleteSubaccountModal />
			) : showPnlModal && pnlDisplayPosition ? (
				<PnlModal
					onClose={() => {
						hideModal('showPnlModal');
					}}
					modalPosition={pnlDisplayPosition}
				/>
			) : showEditOrderModal ? (
				<EditOrderModal />
			) : showInsuranceFundDetailsModal ? (
				<InsuranceFundDetailsModal />
			) : showMarketDetailsModal ? (
				<MarketDetailsModal />
			) : showTradeDetailsModal && isMobile ? (
				<TradeDetailsModal />
			) : showCreateReferralLinkModal ? (
				<CreateReferralLinkModal />
			) : showDelegateAccountModal ? (
				<DelegateModal />
			) : showAssetDetailsModal ? (
				<AssetDetailsModal />
			) : showLpDetailsModal ? (
				<LPDetailsModal />
			) : showLpPerformanceModal7d ? (
				<LpPerformanceModal timeframeIndex={1} />
			) : showLpPerformanceModal30d ? (
				<LpPerformanceModal timeframeIndex={2} />
			) : showDlpIntroModal ? (
				<DlpIntroModal />
			) : showNewLpSubaccountModal ? (
				<NewLpSubaccountModal />
			) : showIfPerformanceModal ? (
				<IFPerformanceModal />
			) : showUserCumFundingModal ? (
				<UserCumFundingModal />
			) : showAccountHealthModal ? (
				<AccountHealthModal />
			) : showInsuranceFundRevenueModal ? (
				<InsuranceFundRevenueModal />
			) : showSendFromWalletModal ? (
				<SendReceiveModal />
			) : showReceiveToWalletModal ? (
				<ReceiveToWalletModal />
			) : showSweepStakeModal ? (
				<SweepstakesAnnouncementModal />
			) : showSweepStakeWinnerModal ? (
				<SweepstakeWinnerModal />
			) : showCloseAllPositionsModal ? (
				<CloseAllPositionsModal />
			) : rateHistoryModal?.show ? (
				<RateHistoryModal
					marketIndex={rateHistoryModal?.marketIndex}
					type={rateHistoryModal?.type}
				/>
			) : showLiquidationModal ? (
				<LiquidationPanel />
			) : showOrderModal ? (
				<OrderPanel />
			) : showLeaguesModal ? (
				<LeaguesModal />
			) : showAccountsSidebarModal ? (
				// This should be the last modal in the list as much as possible, because there may be states
				// where it is transitioning to closing while another modal is opened. This means this large
				// if statement will short-circuit at the first modal that is open, and we want it to be the
				// primary modal displayed.
				<AccountSidebarModal />
			) : showPreLaunchMarketWarningAlert ? (
				<PreLaunchMarketWarningAlert />
			) : (
				<></>
			)}

			{/* Notifications */}
			<ToastList />

			{/* Feature Alerts */}
			<FeatureAlerts />

			{/* Render loading bar */}
			<PageBottomLoadingBar
				visible={
					Object.values(loadingQueue).filter(
						(entry) => entry.isLoading === true
					).length > 0 || showLoadingBar
				}
			/>
		</>
	);
};

export default React.memo(FloatingUI);
